import { create } from 'zustand';
import apiClient from '../lib/api';

export type CandidateStage = 'Applied' | 'Screening' | 'Technical' | 'Manager' | 'Offer' | 'Hired' | 'Rejected';

export interface JobOpening {
    id: string;
    title: string;
    department: string;
    status: 'Open' | 'Closed' | 'Draft' | 'On-Hold';
    candidatesCount: number;
    priority?: 'Low' | 'Medium' | 'High' | 'Urgent';
    posted?: string;
}

export interface Candidate {
    id: string;
    name: string;
    role: string;
    stage: CandidateStage;
    rating: number;
    appliedDate: string;
}

export interface OnboardingTask {
    id: string;
    title: string;
    phase: string;
    category: string;
    assignee_type: string;
    status: 'pending' | 'in-progress' | 'completed';
}

export interface OnboardingProcess {
    id: string;
    first_name: string;
    last_name: string;
    job_title: string;
    status: string;
    tasks: OnboardingTask[];
}

interface RecruitmentState {
    jobs: JobOpening[];
    candidates: Candidate[];
    onboarding: OnboardingProcess[];
    isLoading: boolean;
    error: string | null;
    fetchJobs: () => Promise<void>;
    fetchCandidates: () => Promise<void>;
    fetchOnboarding: () => Promise<void>;
    updateCandidateStage: (id: string, stage: CandidateStage) => Promise<void>;
    updateOnboardingTask: (taskId: string, status: string) => Promise<void>;
    createJob: (jobData: any) => Promise<void>;
    updateJob: (id: string, jobData: any) => Promise<void>;
    deleteJob: (id: string) => Promise<void>;
    deleteCandidate: (id: string) => Promise<void>;
}

export const useRecruitmentStore = create<RecruitmentState>((set, get) => ({
    jobs: [],
    candidates: [],
    onboarding: [],
    isLoading: false,
    error: null,
    fetchJobs: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/recruitment/jobs');
            set({ jobs: (response.data?.data ?? []), isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch jobs', isLoading: false });
        }
    },
    fetchCandidates: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/recruitment/candidates');
            set({ candidates: (response.data?.data ?? []), isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch candidates', isLoading: false });
        }
    },
    fetchOnboarding: async () => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.get('/people/recruitment/onboarding/details');
            set({ onboarding: (response.data?.data ?? []), isLoading: false });
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to fetch onboarding', isLoading: false });
        }
    },
    updateCandidateStage: async (id, stage) => {
        try {
            await apiClient.patch(`/people/recruitment/candidates/${id}/stage`, { stage });
            set((state) => ({
                candidates: state.candidates.map((c) => (c.id === id ? { ...c, stage } : c)),
            }));
            // If candidate is hired, refresh onboarding
            if (stage === 'Hired') {
                get().fetchOnboarding();
            }
        } catch (error: any) {
            console.error('Failed to update candidate stage', error);
        }
    },
    updateOnboardingTask: async (taskId, status) => {
        try {
            await apiClient.patch(`/people/recruitment/onboarding/tasks/${taskId}`, { status });
            set((state) => ({
                onboarding: state.onboarding.map(o => ({
                    ...o,
                    tasks: o.tasks.map(t => t.id === taskId ? { ...t, status: status as OnboardingTask['status'] } : t)
                }))
            }));
        } catch (error: any) {
            console.error('Failed to update onboarding task', error);
        }
    },
    createJob: async (jobData) => {
        set({ isLoading: true, error: null });
        try {
            const response = await apiClient.post('/people/recruitment/jobs', jobData);
            set((state) => ({
                jobs: [(response.data?.data ?? response.data), ...state.jobs],
                isLoading: false,
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to create job', isLoading: false });
            throw error;
        }
    },
    updateJob: async (id, jobData) => {
        set({ error: null });
        try {
            const response = await apiClient.patch(`/people/recruitment/jobs/${id}`, jobData);
            const updated = response.data?.data ?? response.data;
            set((state) => ({
                jobs: state.jobs.map((j) => (j.id === id ? { ...j, ...updated } : j)),
            }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to update job' });
            throw error;
        }
    },
    deleteJob: async (id) => {
        set({ error: null });
        try {
            await apiClient.delete(`/people/recruitment/jobs/${id}`);
            set((state) => ({ jobs: state.jobs.filter((j) => j.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete job' });
            throw error;
        }
    },
    deleteCandidate: async (id) => {
        set({ error: null });
        try {
            await apiClient.delete(`/people/recruitment/candidates/${id}`);
            set((state) => ({ candidates: state.candidates.filter((c) => c.id !== id) }));
        } catch (error: any) {
            set({ error: error.response?.data?.message || 'Failed to delete candidate' });
            throw error;
        }
    },
}));
